(() => {
  if (window.__heatedBlanketContentInitialized) return;
  window.__heatedBlanketContentInitialized = true;

  const sectionSelector = '[data-blanket-section]';
  const sections = new Map();
  let cartObserver;
  let cartObserverTarget;

  const isBlanketPage = () => document.body?.matches('[data-product-page="heated-blanket"]') || document.querySelector('[data-blanket-section="00-header"]');
  const findOwned = (section, selector) => Array.from(section.querySelectorAll(selector)).filter((element) => element.closest(sectionSelector) === section);

  function syncCartCount() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    const source = bubble?.querySelector('span[aria-hidden="true"]') || Array.from(bubble?.querySelectorAll('span') || []).find((span) => !span.classList.contains('visually-hidden') && !span.hidden);
    const value = source?.textContent.trim() || '0';
    sections.forEach((_, section) => {
      findOwned(section, '[data-blanket-cart-count]').forEach((count) => {
        if (count.textContent !== value) count.textContent = value;
        count.hidden = !bubble || value === '0';
      });
    });
  }

  function refreshCartObserver() {
    const hasCounts = Array.from(sections.keys()).some((section) => findOwned(section, '[data-blanket-cart-count]').length);
    const target = hasCounts ? document.querySelector('#cart-icon-bubble')?.parentElement : null;
    if (target !== cartObserverTarget) {
      cartObserver?.disconnect();
      cartObserverTarget = target;
      if (target) {
        cartObserver = new MutationObserver(syncCartCount);
        cartObserver.observe(target, { childList: true, characterData: true, subtree: true });
      }
    }
    syncCartCount();
  }

  function setupMosaic(mosaic, signal) {
    const cards = Array.from(mosaic.children).filter((card) => card.matches('article.blanket-editorial-card'));
    if (!cards.length || !window.ResizeObserver) return () => {};
    let frame = 0;
    let lastWidth = -1;

    function layout() {
      frame = 0;
      if (signal.aborted || !mosaic.isConnected) return;
      const width = mosaic.clientWidth;
      if (!width) return;
      const style = getComputedStyle(mosaic);
      const columns = Math.max(2, Math.min(5, parseInt(style.getPropertyValue('--blanket-columns'), 10) || 2));
      const gap = parseFloat(style.columnGap) || 14;
      const cardWidth = (width - gap * (columns - 1)) / columns;
      const bottoms = Array(columns).fill(0);
      mosaic.classList.add('blanket-mosaic-ready');
      cards.forEach((card) => { card.style.width = `${cardWidth}px`; });
      cards.forEach((card) => {
        const column = bottoms.indexOf(Math.min(...bottoms));
        card.style.left = `${column * (cardWidth + gap)}px`;
        card.style.top = `${bottoms[column]}px`;
        bottoms[column] += card.offsetHeight + gap;
      });
      mosaic.style.height = `${Math.max(...bottoms) - gap}px`;
    }

    function scheduleLayout() {
      if (!signal.aborted && !frame) frame = requestAnimationFrame(layout);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined && Math.abs(width - lastWidth) > 0.5) {
        lastWidth = width;
        scheduleLayout();
      }
    });
    resizeObserver.observe(mosaic);
    mosaic.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', scheduleLayout, { signal });
      image.addEventListener('error', scheduleLayout, { signal });
    });
    document.fonts?.ready.then(scheduleLayout);
    scheduleLayout();

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(frame);
      mosaic.classList.remove('blanket-mosaic-ready');
      mosaic.style.removeProperty('height');
      cards.forEach((card) => {
        ['width', 'left', 'top'].forEach((property) => card.style.removeProperty(property));
      });
    };
  }

  function initializeSection(section) {
    if (sections.has(section)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanup = [];
    sections.set(section, () => {
      controller.abort();
      cleanup.forEach((destroy) => destroy());
    });

    findOwned(section, '[data-blanket-menu-toggle]').forEach((button) => {
      const menuId = button.getAttribute('aria-controls');
      const menu = menuId === 'BlanketMobileMenu' ? section.querySelector('#BlanketMobileMenu') : null;
      if (!menu) return;
      const setOpen = (open) => {
        menu.hidden = !open;
        button.setAttribute('aria-expanded', String(open));
      };
      setOpen(!menu.hidden);
      button.addEventListener('click', () => setOpen(menu.hidden), { signal });
      section.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !menu.hidden) {
          setOpen(false);
          button.focus();
        }
      }, { signal });
      menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false);
      }, { signal });
    });

    findOwned(section, '[data-blanket-cart]').forEach((button) => {
      button.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (typeof drawer?.open !== 'function') return;
        event.preventDefault();
        drawer.open(button);
      }, { signal });
    });

    findOwned(section, '.dropdowns').forEach((group) => {
      const details = Array.from(group.querySelectorAll('details.dropdown-single')).filter((detail) => detail.closest('.dropdowns') === group);
      details.forEach((detail) => {
        const summary = detail.querySelector(':scope > summary');
        summary?.addEventListener('click', (event) => {
          const intendedOpen = !detail.open;
          event.preventDefault();
          details.forEach((other) => { if (other !== detail) other.open = false; });
          detail.open = intendedOpen;
        }, { signal });
      });
    });

    findOwned(section, '[data-blanket-mosaic]').forEach((mosaic) => cleanup.push(setupMosaic(mosaic, signal)));
  }

  function initializeWithin(root) {
    if (!isBlanketPage()) return;
    if (root.matches?.(sectionSelector)) initializeSection(root);
    root.querySelectorAll(sectionSelector).forEach(initializeSection);
    refreshCartObserver();
  }

  function start() {
    if (!isBlanketPage()) return;
    initializeWithin(document);
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href^="#blanket-"]');
      const section = link?.closest(sectionSelector);
      const page = link?.closest('.heatedblanket');
      if (!section?.classList.contains('heated-blanket-content') || !page) return;
      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target || !page.contains(target)) return;
      event.preventDefault();
      const menu = link.closest('#BlanketMobileMenu');
      if (menu) {
        menu.hidden = true;
        section.querySelector('[data-blanket-menu-toggle][aria-controls="BlanketMobileMenu"]')?.setAttribute('aria-expanded', 'false');
      }
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }, true);
    document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      sections.forEach((destroy, section) => {
        if (event.target === section || event.target.contains(section)) {
          destroy();
          sections.delete(section);
        }
      });
      refreshCartObserver();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
